import { useState, type FormEvent } from "react";
import { Aviso, Botao, CampoLongo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoCadastro } from "../../nucleo/cadastro";
import { useCalendariosDeSla, useEquipesDeAtendimento } from "../../nucleo/tickets";
import {
  formularioDaConfiguracao,
  useConfiguracaoDoChat,
  useConfigurarChat,
  type ConfiguracaoDoChat as Configuracao,
  type FormularioDeConfiguracao,
} from "../../nucleo/webchat";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

/** A configuração do chat por organização: liga/desliga, horário, equipe e mensagem. */
export function ConfiguracaoDoChat() {
  const organizacoes = useOrganizacoesDoCadastro();
  const [organizacaoId, setOrganizacaoId] = useState("");
  const configuracao = useConfiguracaoDoChat(organizacaoId);

  return (
    <div data-testid="chat-configuracao">
      <p className="lead mb-4">
        Cada empresa tem o seu chat: você escolhe se está ligado, em que horário atende, qual equipe
        recebe as conversas e o que o cliente lê fora do expediente.
      </p>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="chat-config-organizacao">
          Organização
        </label>
        <select
          id="chat-config-organizacao"
          className="campo__controle"
          value={organizacaoId}
          onChange={(evento) => setOrganizacaoId(evento.target.value)}
        >
          <option value="">Escolha uma organização…</option>
          {(organizacoes.data ?? []).map((organizacao) => (
            <option key={organizacao.id} value={organizacao.id}>
              {nomeDaOrganizacao(organizacao)}
            </option>
          ))}
        </select>
      </div>
      {organizacaoId && configuracao.isPending ? <p role="status">Carregando a configuração…</p> : null}
      {configuracao.isError ? <Aviso tom="erro">{mensagemDoConsole(configuracao.error)}</Aviso> : null}
      {configuracao.data ? (
        <FormularioDaConfiguracao
          // A chave remonta o formulário ao trocar de empresa — e só nisso: remontar depois de
          // salvar apagaria a confirmação "Configuração salva".
          key={configuracao.data.organizacao_id}
          organizacaoId={organizacaoId}
          atual={configuracao.data}
        />
      ) : null}
    </div>
  );
}

function FormularioDaConfiguracao({
  organizacaoId,
  atual,
}: {
  organizacaoId: string;
  atual: Configuracao;
}) {
  const calendarios = useCalendariosDeSla();
  const equipes = useEquipesDeAtendimento();
  const salvar = useConfigurarChat(organizacaoId);
  const voltarParaHome = useVoltarParaHome();
  const [form, setForm] = useState<FormularioDeConfiguracao>(formularioDaConfiguracao(atual));

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(form, { onSuccess: () => voltarParaHome("Configuração do chat salva.") });
  }

  return (
    <Cartao titulo="Como o chat funciona" data-testid="chat-configuracao-formulario">
      <form onSubmit={enviar}>
        <label className="opcao">
          <input
            type="checkbox"
            checked={form.ativo}
            onChange={(evento) => setForm({ ...form, ativo: evento.target.checked })}
          />
          <span className="flex-1">Chat ligado para esta empresa</span>
        </label>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="chat-config-calendario">
            Horário de atendimento
          </label>
          <select
            id="chat-config-calendario"
            className="campo__controle"
            value={form.calendarioId}
            onChange={(evento) => setForm({ ...form, calendarioId: evento.target.value })}
          >
            <option value="">O calendário de SLA da empresa (ou sempre aberto)</option>
            {(calendarios.data ?? [])
              .filter((calendario) => calendario.organizacao_id === organizacaoId)
              .map((calendario) => (
                <option key={calendario.id} value={calendario.id}>
                  {calendario.nome}
                </option>
              ))}
          </select>
        </div>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="chat-config-equipe">
            Equipe que recebe as conversas
          </label>
          <select
            id="chat-config-equipe"
            className="campo__controle"
            value={form.equipeId}
            onChange={(evento) => setForm({ ...form, equipeId: evento.target.value })}
          >
            <option value="">Todas as equipes veem a fila</option>
            {(equipes.data ?? []).map((equipe) => (
              <option key={equipe.id} value={equipe.id}>
                {equipe.nome}
              </option>
            ))}
          </select>
        </div>
        <CampoLongo
          rotulo="Mensagem fora do horário"
          ajuda="Aparece para o cliente quando ele escreve fora do expediente. Vazio usa o texto padrão."
          value={form.mensagem}
          maxLength={1000}
          onChange={(evento) => setForm({ ...form, mensagem: evento.target.value })}
        />
        <label className="opcao">
          <input
            type="checkbox"
            checked={form.digitando}
            onChange={(evento) => setForm({ ...form, digitando: evento.target.checked })}
          />
          <span className="flex-1">Mostrar “digitando…” dos dois lados</span>
        </label>
        <p className="nota-rodape mt-2">
          Agora: {atual.aberto_agora ? "dentro do horário de atendimento." : "fora do horário de atendimento."}
        </p>
        {salvar.isError ? (
          <Aviso tom="erro" className="mt-3">
            {mensagemDoConsole(salvar.error)}
          </Aviso>
        ) : null}
        <Botao type="submit" className="mt-3" disabled={salvar.isPending} data-testid="chat-configuracao-salvar">
          Salvar configuração
        </Botao>
      </form>
    </Cartao>
  );
}
